import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Version {
  id: string
  version: string
  status: string
  released_at: string
  deprecated_at: string | null
  sunset_at: string | null
  endpoints: number
  active_clients: number
}

interface Deprecation {
  id: string
  endpoint: string
  version: string
  reason: string
  deprecated_at: string
  sunset_at: string
  replacement: string
  usage_count: number
}

export default function APIVersioningPage() {
  const [versions, setVersions] = useState<Version[]>([])
  const [deprecations, setDeprecations] = useState<Deprecation[]>([])
  const [changelog, setChangelog] = useState<any[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [activeTab, setActiveTab] = useState<'versions' | 'deprecations' | 'changelog'>('versions')

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [verRes, depRes, chgRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/api-versions/versions'),
      fetch(API_BASE + '/api-versions/deprecations'),
      fetch(API_BASE + '/api-versions/changelog'),
      fetch(API_BASE + '/api-versions/statistics')
    ])
    if (verRes.ok) setVersions((await verRes.json()).versions || [])
    if (depRes.ok) setDeprecations((await depRes.json()).deprecations || [])
    if (chgRes.ok) setChangelog((await chgRes.json()).changelog || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'stable': return 'bg-green-100 text-green-800'
      case 'beta': return 'bg-blue-100 text-blue-800'
      case 'deprecated': return 'bg-yellow-100 text-yellow-800'
      case 'sunset': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getChangeTypeColor = (type: string) => {
    switch (type) {
      case 'added': return 'bg-green-100 text-green-800'
      case 'improved': return 'bg-blue-100 text-blue-800'
      case 'breaking': return 'bg-red-100 text-red-800'
      case 'deprecated': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">API Versioning & Deprecation</h1>

        {statistics && (
          <div className="grid grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Versions</div>
              <div className="text-2xl font-bold">{statistics.total_versions}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active Versions</div>
              <div className="text-2xl font-bold text-green-600">{statistics.active_versions}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Deprecated</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.deprecated_versions}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Current Stable</div>
              <div className="text-2xl font-bold text-blue-600">v{statistics.current_stable}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Latest</div>
              <div className="text-2xl font-bold">v{statistics.latest_version}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Deprecations</div>
              <div className="text-2xl font-bold text-red-600">{statistics.total_deprecations}</div>
            </div>
          </div>
        )}

        <div className="flex gap-4 mb-6">
          <button onClick={() => setActiveTab('versions')} className={`px-4 py-2 rounded-lg ${activeTab === 'versions' ? 'bg-blue-600 text-white' : 'bg-white border'}`}>Versions</button>
          <button onClick={() => setActiveTab('deprecations')} className={`px-4 py-2 rounded-lg ${activeTab === 'deprecations' ? 'bg-blue-600 text-white' : 'bg-white border'}`}>Deprecations</button>
          <button onClick={() => setActiveTab('changelog')} className={`px-4 py-2 rounded-lg ${activeTab === 'changelog' ? 'bg-blue-600 text-white' : 'bg-white border'}`}>Changelog</button>
        </div>

        {activeTab === 'versions' && (
          <div className="grid grid-cols-3 gap-6">
            {versions.map(ver => (
              <div key={ver.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="text-3xl font-bold">v{ver.version}</div>
                  <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(ver.status)}`}>{ver.status}</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Endpoints</span>
                    <span className="font-medium">{ver.endpoints}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Active Clients</span>
                    <span className="font-medium">{ver.active_clients}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Released</span>
                    <span>{new Date(ver.released_at).toLocaleDateString()}</span>
                  </div>
                  {ver.deprecated_at && (
                    <div className="flex justify-between text-yellow-600">
                      <span>Deprecated</span>
                      <span>{new Date(ver.deprecated_at).toLocaleDateString()}</span>
                    </div>
                  )}
                  {ver.sunset_at && (
                    <div className="flex justify-between text-red-600">
                      <span>Sunset</span>
                      <span>{new Date(ver.sunset_at).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'deprecations' && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Deprecated Endpoints</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Endpoint</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Version</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Replacement</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sunset</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {deprecations.map(dep => (
                  <tr key={dep.id}>
                    <td className="px-6 py-4 font-mono text-sm">{dep.endpoint}</td>
                    <td className="px-6 py-4">v{dep.version}</td>
                    <td className="px-6 py-4 text-sm">{dep.reason}</td>
                    <td className="px-6 py-4 font-mono text-sm text-blue-600">{dep.replacement}</td>
                    <td className="px-6 py-4">{dep.usage_count}</td>
                    <td className="px-6 py-4 text-sm">{new Date(dep.sunset_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'changelog' && (
          <div className="space-y-6">
            {changelog.map((entry, idx) => (
              <div key={idx} className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center gap-4 mb-4">
                  <span className="text-2xl font-bold">v{entry.version}</span>
                  <span className="text-gray-500">{new Date(entry.date).toLocaleDateString()}</span>
                </div>
                <div className="space-y-2">
                  {entry.changes.map((change: any, i: number) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded text-xs capitalize ${getChangeTypeColor(change.type)}`}>{change.type}</span>
                      <span>{change.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  )
}
